import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
} from 'react-router';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  BadgeCheck,
  Bot,
  Cable,
  Gauge,
  Gift,
  Globe2,
  History,
  LifeBuoy,
  LogOut,
  MessageCircleQuestion,
  Megaphone,
  ScrollText,
  Settings as SettingsIcon,
  Star,
  UserCog,
  Undo2,
  Users as UsersIcon,
  Wallet,
  Sparkles,
} from 'lucide-react';
import type { Permission } from '@gaintplay/core/permissions';
import './index.css';
import { installPressFeedback } from './lib/press';
import { api } from './lib/api';
import { staffQuery, useCan } from './lib/staff';
import { DialogHost, NoAccess, Toaster, useScrollCue } from './components/ui';
import { Assistant } from './components/Assistant';
import { useAssistantOpen } from './lib/assistant';
import { Login, Setup } from './pages/Auth';
import { Overview } from './pages/Overview';
import { Withdrawals } from './pages/Withdrawals';
import { GiftCards } from './pages/GiftCards';
import { Providers } from './pages/Providers';
import { Countries } from './pages/Countries';
import { Offers } from './pages/Offers';
import { Postbacks } from './pages/Postbacks';
import { Reversals } from './pages/Reversals';
import { Users, UserDetail } from './pages/Users';
import { Decisions } from './pages/Decisions';
import { Legacy } from './pages/Legacy';
import { LegacyApprovals } from './pages/LegacyApprovals';
import { Support, SupportTicket } from './pages/Support';
import { Settings } from './pages/Settings';
import { Audit } from './pages/Audit';
import { PlayStore } from './pages/PlayStore';
import { Team } from './pages/Team';
import { Faq, FaqFromTicket } from './pages/Faq';
import { NotificationCampaign, Notifications } from './pages/Notifications';

const qc = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: true } },
});

function useStaff() {
  return useQuery(staffQuery);
}

interface Attention {
  attention: {
    ticketsNeedStaff: number;
    openFlags: number;
    legacyNeedReview: number;
    legacyApprovals: number;
    /** The approvals page is temporary: while anything waits, or until 30 days after the deadline. */
    legacyApprovalsVisible: boolean;
    withdrawalsOnHold: number;
    playNeedsReply: number;
  };
}

type AttentionKey =
  | 'ticketsNeedStaff'
  | 'openFlags'
  | 'legacyNeedReview'
  | 'legacyApprovals'
  | 'withdrawalsOnHold'
  | 'playNeedsReply';
interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  /** Listed only for staff whose roles include it (the page and the API check it too). */
  perm: Permission;
  end?: boolean;
  badge?: AttentionKey;
  /** Only listed when this says so (temporary sections, sections a role can't use). */
  show?: (a: Attention['attention'] | undefined) => boolean;
}

const groups: { title: string; items: NavItem[] }[] = [
  {
    title: 'Money',
    items: [
      { to: '/', label: 'Overview', icon: Gauge, end: true, perm: 'overview.view' },
      {
        to: '/withdrawals',
        label: 'Withdrawals',
        icon: Wallet,
        badge: 'withdrawalsOnHold',
        perm: 'withdrawals.view',
      },
      { to: '/giftcards', label: 'Gift cards', icon: Gift, perm: 'giftcards.view' },
      { to: '/decisions', label: 'Decisions', icon: Bot, perm: 'decisions.view' },
    ],
  },
  {
    title: 'People',
    items: [
      {
        to: '/support',
        label: 'Support',
        icon: LifeBuoy,
        badge: 'ticketsNeedStaff',
        perm: 'support.view',
      },
      { to: '/faq', label: 'FAQ', icon: MessageCircleQuestion, perm: 'faq.manage' },
      { to: '/users', label: 'Users', icon: UsersIcon, badge: 'openFlags', perm: 'users.view' },
      { to: '/notifications', label: 'Notifications', icon: Megaphone, perm: 'notifications.send' },
      { to: '/play', label: 'Play Store', icon: Star, badge: 'playNeedsReply', perm: 'play.view' },
      {
        to: '/legacy/approvals',
        label: 'Old account approvals',
        icon: BadgeCheck,
        badge: 'legacyApprovals',
        perm: 'legacy.view',
        show: (a) => !!a?.legacyApprovalsVisible,
      },
      {
        to: '/legacy',
        label: 'Old accounts',
        icon: History,
        end: true,
        badge: 'legacyNeedReview',
        perm: 'legacy.view',
      },
    ],
  },
  {
    title: 'Partners',
    items: [
      { to: '/providers', label: 'Networks', icon: Cable, perm: 'providers.view' },
      { to: '/offers', label: 'Offers on Earn', icon: Sparkles, perm: 'providers.view' },
      { to: '/countries', label: 'Countries', icon: Globe2, perm: 'providers.view' },
      { to: '/postbacks', label: 'Postback log', icon: Activity, perm: 'postbacks.view' },
      { to: '/reversals', label: 'Reversals', icon: Undo2, perm: 'reversals.apply' },
    ],
  },
  {
    title: 'System',
    items: [
      { to: '/settings', label: 'Settings', icon: SettingsIcon, perm: 'settings.view' },
      { to: '/audit', label: 'Audit log', icon: ScrollText, perm: 'audit.view' },
      { to: '/team', label: 'Team', icon: UserCog, perm: 'staff.manage' },
    ],
  },
];
const nav = groups.flatMap((g) => g.items);
const shown = (
  items: NavItem[],
  a: Attention['attention'] | undefined,
  can: (p: Permission) => boolean,
) => items.filter((n) => can(n.perm) && (!n.show || n.show(a)));

/** The counts, for every role. Under the Overview's key, so pages that refresh it refresh these. */
function useAttention(signedIn: boolean) {
  return useQuery({
    queryKey: ['overview', 'attention'],
    queryFn: () => api<Attention>('/attention'),
    refetchInterval: 30_000,
    enabled: signedIn,
  }).data?.attention;
}

/** What each sidebar count means, read out after the number. */
const BADGE_LABEL: Record<AttentionKey, string> = {
  withdrawalsOnHold: 'on hold',
  ticketsNeedStaff: 'need staff',
  openFlags: 'open flags',
  legacyNeedReview: 'to review',
  legacyApprovals: 'to approve',
  playNeedsReply: 'reviews to answer',
};

function Badge({ n, what }: { n: number | undefined; what: AttentionKey }) {
  if (!n) return null;
  return (
    <span
      className="relative ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-coin px-1.5 text-[11px] font-extrabold tabular-nums text-ink"
      title={`${n} ${BADGE_LABEL[what]}`}
    >
      <span aria-hidden="true">{n > 99 ? '99+' : n}</span>
      <span className="sr-only">
        , {n} {BADGE_LABEL[what]}
      </span>
    </span>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <img src="/brand/logo-48.png" alt="" className="h-8 w-8" />
      <span className="text-[18px] font-extrabold tracking-[-0.03em]">GaintPlay</span>
      <span className="rounded-full bg-ink px-2 py-0.5 text-[11px] font-bold text-white">
        Admin
      </span>
    </div>
  );
}

/** Mobile/tablet section strip: scrolls sideways, fades the clipped edge, and keeps the current
 *  section in view after navigation. */
function SectionStrip({ attention }: { attention: Attention['attention'] | undefined }) {
  const ref = useScrollCue<HTMLElement>();
  const can = useCan();
  const { pathname } = useLocation();
  useEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('[aria-current=page]');
    const strip = ref.current;
    if (!el || !strip) return;
    const left = el.offsetLeft - (strip.clientWidth - el.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'instant' as ScrollBehavior });
  }, [pathname, ref]);
  return (
    <nav
      ref={ref}
      aria-label="Sections"
      className="scroll-cue no-scrollbar -mx-3 mt-2 flex gap-1.5 overflow-x-auto px-3 pb-1 sm:-mx-6 sm:px-6"
    >
      {shown(nav, attention, can).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end ?? false}
          className={({ isActive }) =>
            `flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[13px] font-semibold transition ${
              isActive
                ? 'border-ink bg-ink text-white'
                : 'border-hairline bg-paper text-graphite hover:border-ink hover:text-ink'
            }`
          }
        >
          {n.label}
          {n.badge && <Badge n={attention?.[n.badge]} what={n.badge} />}
        </NavLink>
      ))}
    </nav>
  );
}

function Shell() {
  const { data: staff, isLoading, isFetching } = useStaff();
  const navigate = useNavigate();
  const client = useQueryClient();
  const attention = useAttention(!!staff);
  const can = useCan();
  const here = useLocation();
  const assistantOpen = useAssistantOpen();
  // "Signed out" from before a sign-in may still be cached while the fresh answer is on its way:
  // wait for it instead of bouncing back to the sign-in page.
  if (isLoading || (!staff && isFetching))
    return (
      <div className="grid min-h-screen place-items-center" role="status" aria-label="Loading">
        <img src="/brand/logo-48.png" alt="" className="h-10 w-10 animate-pulse" />
      </div>
    );
  if (!staff) {
    const next = here.pathname === '/' ? '' : here.pathname + here.search;
    return <Navigate to={next ? `/login?next=${encodeURIComponent(next)}` : '/login'} replace />;
  }
  const logout = async () => {
    await api('/auth/logout', { body: {} }).catch(() => {});
    client.clear();
    navigate('/login');
  };
  // Only what this person can open; a section with nothing in it isn't listed.
  const sections = groups
    .map((g) => ({ ...g, items: shown(g.items, attention, can) }))
    .filter((g) => g.items.length);
  // The admin assistant, for staff allowed to use it; on wide screens the page makes room for it.
  const assistant = staff.permissions.includes('assistant.use');
  const itemClass = ({ isActive }: { isActive: boolean }) =>
    `flex min-h-9 items-center gap-2.5 rounded-[10px] px-3 py-1.5 text-[14px] font-semibold leading-tight transition ${
      isActive ? 'bg-ink text-white' : 'text-graphite hover:bg-canvas hover:text-ink'
    }`;
  return (
    <div className="min-h-screen lg:flex">
      <a
        href="#main"
        className="sr-only z-[80] rounded-[10px] bg-ink px-4 py-2 text-[14px] font-semibold text-white focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to content
      </a>
      {/* Desktop: a floating sticker panel, the admin twin of the web app's header. */}
      <aside className="sticky top-0 hidden h-screen w-[264px] shrink-0 p-4 pr-0 lg:block">
        <div className="flex h-full flex-col rounded-[20px] border-[1.5px] border-ink bg-paper shadow-[4px_4px_0_#111110]">
          <div className="border-b border-hairline px-4 py-4">
            <Brand />
          </div>
          <nav
            aria-label="Sections"
            className="no-scrollbar flex-1 space-y-5 overflow-y-auto px-3 py-4"
          >
            {sections.map((g) => (
              <div key={g.title} role="group" aria-labelledby={`nav-${g.title}`}>
                <div
                  id={`nav-${g.title}`}
                  className="mb-1 px-3 text-[12px] font-semibold text-stone"
                >
                  {g.title}
                </div>
                <div className="space-y-0.5">
                  {g.items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end ?? false} className={itemClass}>
                      <n.icon size={16} strokeWidth={1.75} aria-hidden="true" /> {n.label}
                      {n.badge && <Badge n={attention?.[n.badge]} what={n.badge} />}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="flex items-center gap-3 border-t border-hairline p-3">
            <span
              aria-hidden="true"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-canvas text-[14px] font-extrabold uppercase"
            >
              {staff.name[0]}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-bold">{staff.name}</div>
              <div className="truncate text-[12px] text-stone" title={staff.email}>
                {staff.email}
              </div>
            </div>
            <button
              type="button"
              onClick={logout}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-stone transition hover:bg-canvas hover:text-berry-deep"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className={`min-w-0 flex-1 ${assistant && assistantOpen ? 'xl:mr-[468px]' : ''}`}>
        {/* Phones and tablets: floating bar plus a scrollable row of sections. */}
        <div className="sticky top-0 z-30 bg-canvas px-3 pb-2 pt-3 sm:px-6 lg:hidden">
          <div className="flex h-14 items-center rounded-[16px] border-[1.5px] border-ink bg-paper pl-3 pr-2 shadow-[3px_3px_0_#111110]">
            <Brand />
            <span className="ml-auto mr-1 hidden truncate text-[13px] font-semibold text-stone sm:block">
              {staff.name}
            </span>
            <button
              type="button"
              onClick={logout}
              className="ml-auto grid h-10 w-10 place-items-center rounded-[10px] text-stone transition hover:bg-canvas hover:text-berry-deep sm:ml-0"
              aria-label="Log out"
              title="Log out"
            >
              <LogOut size={18} />
            </button>
          </div>
          <SectionStrip attention={attention} />
        </div>
        <main
          id="main"
          tabIndex={-1}
          className="mx-auto max-w-[1200px] px-4 pb-16 pt-4 outline-none sm:px-6 lg:px-8 lg:pt-8"
        >
          <Outlet />
        </main>
        <DialogHost />
        <Toaster />
      </div>
      {assistant && <Assistant />}
    </div>
  );
}

/** A page, or a friendly "no access" when this staff member's roles don't include it. */
function Gate({ children, need }: { children: ReactNode; need: Permission }) {
  return useCan()(need) ? <>{children}</> : <NoAccess need={need} />;
}
const page = (need: Permission, element: ReactNode) => <Gate need={need}>{element}</Gate>;

/** The first page: the Overview, or else the first section this person's roles include. */
function Home() {
  const can = useCan();
  if (can('overview.view')) return <Overview />;
  const first = nav.find((n) => can(n.perm));
  return first ? <Navigate to={first.to} replace /> : <NoAccess />;
}

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/setup', element: <Setup /> },
  {
    element: <Shell />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/withdrawals', element: page('withdrawals.view', <Withdrawals />) },
      { path: '/giftcards', element: page('giftcards.view', <GiftCards />) },
      { path: '/decisions', element: page('decisions.view', <Decisions />) },
      { path: '/legacy', element: page('legacy.view', <Legacy />) },
      { path: '/legacy/approvals', element: page('legacy.view', <LegacyApprovals />) },
      { path: '/support', element: page('support.view', <Support />) },
      { path: '/support/:id', element: page('support.view', <SupportTicket />) },
      { path: '/notifications', element: page('notifications.send', <Notifications />) },
      { path: '/notifications/:id', element: page('notifications.send', <NotificationCampaign />) },
      { path: '/faq', element: page('faq.manage', <Faq />) },
      { path: '/faq/from-ticket/:id', element: page('faq.manage', <FaqFromTicket />) },
      { path: '/users', element: page('users.view', <Users />) },
      { path: '/users/:id', element: page('users.view', <UserDetail />) },
      { path: '/providers', element: page('providers.view', <Providers />) },
      { path: '/offers', element: page('providers.view', <Offers />) },
      { path: '/countries', element: page('providers.view', <Countries />) },
      { path: '/postbacks', element: page('postbacks.view', <Postbacks />) },
      { path: '/reversals', element: page('reversals.apply', <Reversals />) },
      { path: '/settings', element: page('settings.view', <Settings />) },
      { path: '/audit', element: page('audit.view', <Audit />) },
      { path: '/play', element: page('play.view', <PlayStore />) },
      { path: '/team', element: page('staff.manage', <Team />) },
    ],
  },
]);

installPressFeedback();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
